import { usePage } from "@inertiajs/react";
import { useMediaQuery } from "@uidotdev/usehooks";
import { format } from "date-fns";
import { ExternalLinkIcon, ReceiptTextIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import ApptPagination from "@/components/admin-portal/appointment/pagination-list";
import { useDateContext } from "@/components/providers/date-provider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from "@/components/ui/drawer";
import { Skeleton } from "@/components/ui/skeleton";
import {
	orderStatusStyle,
	paymentStatusStyle,
} from "@/lib/orders/status-styles";
import { cn, formatCurrency } from "@/lib/utils";
import type { VoucherOrder } from "@/types/admin-portal/vouchers";
import type { GlobalPageProps } from "@/types/globals";
import type { Metadata } from "@/types/pagy";

interface VoucherOrdersDialogProps {
	voucherId: string;
	voucherCode: string;
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
}

interface VoucherOrdersResponse {
	orders: VoucherOrder[];
	metadata: Metadata | null;
}

const DEFAULT_LIMIT = 10;

function LabeledBadge({
	label,
	value,
	className,
}: {
	label: string;
	value: string | null;
	className?: string;
}) {
	return (
		<Badge
			variant="outline"
			className={cn(
				"gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase",
				className,
			)}
		>
			<span className="opacity-60">{label}:</span>
			<span>{(value ?? "-").replaceAll("_", " ")}</span>
		</Badge>
	);
}

function FieldItem({
	label,
	value,
	className,
}: {
	label: string;
	value: string;
	className?: string;
}) {
	return (
		<div className={cn("min-w-0 space-y-0.5", className)}>
			<dt className="text-[10px] font-semibold uppercase text-muted-foreground">
				{label}
			</dt>
			<dd className="font-medium text-pretty">{value}</dd>
		</div>
	);
}

function OrderCard({
	order,
	onOpen,
	formatDate,
}: {
	order: VoucherOrder;
	onOpen: (registrationNumber: string) => void;
	formatDate: (value: string | null) => string;
}) {
	const packageLabel = [
		order.serviceName?.replaceAll("_", " "),
		order.packageName,
	]
		.filter(Boolean)
		.join(" • ");
	const packageValue = packageLabel
		? `${packageLabel}${order.numberOfVisit != null ? ` — ${order.numberOfVisit} Visit` : ""}`
		: "N/A";

	return (
		<button
			type="button"
			onClick={() => onOpen(order.registrationNumber)}
			className="flex flex-col w-full gap-3 p-3 text-left transition-colors border rounded-lg group bg-background hover:bg-muted/50 hover:border-primary/30"
		>
			<div className="flex items-start justify-between gap-2">
				<p className="flex items-center min-w-0 gap-1 font-semibold">
					<span className="truncate">{order.registrationNumber}</span>
					<ExternalLinkIcon className="size-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-70" />
				</p>

				<div className="flex flex-wrap justify-end gap-1 shrink-0">
					<LabeledBadge
						label="Status"
						value={order.status}
						className={orderStatusStyle(order.status)}
					/>
					<LabeledBadge
						label="Payment"
						value={order.paymentStatus}
						className={paymentStatusStyle(order.paymentStatus)}
					/>
				</div>
			</div>

			<dl className="grid grid-cols-1 text-xs gap-x-4 gap-y-2 sm:grid-cols-2">
				<FieldItem label="Created at" value={formatDate(order.createdAt)} />
				<FieldItem label="Patient" value={order.patientName ?? "N/A"} />
				<FieldItem
					label="Therapist"
					value={
						order.therapistNames.length
							? order.therapistNames.join(", ")
							: "N/A"
					}
				/>
				<FieldItem
					label="Visit date & time"
					value={formatDate(order.visitDateTime)}
				/>
				<FieldItem
					label="Package"
					value={packageValue}
					className="sm:col-span-2"
				/>
			</dl>

			<div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 pt-2 text-xs border-t">
				<span className="inline-flex items-center gap-1 min-w-0 text-muted-foreground">
					<ReceiptTextIcon className="size-3.5 shrink-0" />
					<span className="truncate">
						{order.invoiceNumber ?? "No invoice"}
					</span>
				</span>
				<span className="text-muted-foreground">
					Discount:{" "}
					<span className="font-semibold text-foreground">
						{formatCurrency(order.discountAmount, { emptyValue: "N/A" })}
					</span>
				</span>
				<span className="text-muted-foreground">
					Total:{" "}
					<span className="font-semibold text-foreground">
						{formatCurrency(order.totalAmount, { emptyValue: "N/A" })}
					</span>
				</span>
			</div>
		</button>
	);
}

export default function VoucherOrdersDialog({
	voucherId,
	voucherCode,
	isOpen,
	onOpenChange,
}: VoucherOrdersDialogProps) {
	const { props: globalProps } = usePage<GlobalPageProps>();
	const { locale, tzDate } = useDateContext();
	const isDesktop = useMediaQuery("(min-width: 768px)");

	const vouchersIndexRoute =
		globalProps.adminPortal?.router?.adminPortal?.vouchers?.index;
	const ordersIndexRoute =
		globalProps.adminPortal?.router?.adminPortal?.appointment?.orders ??
		"/admin-portal/appointments/orders";

	const [orders, setOrders] = useState<VoucherOrder[]>([]);
	const [metadata, setMetadata] = useState<Metadata | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(DEFAULT_LIMIT);

	// Reset pagination when the dialog closes, so the next open starts at page 1
	// without firing a redundant fetch for a stale page first.
	useEffect(() => {
		if (isOpen) return;
		setPage(1);
		setLimit(DEFAULT_LIMIT);
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen || !voucherId || !vouchersIndexRoute) return;

		const controller = new AbortController();

		const fetchOrders = async () => {
			setIsLoading(true);
			setError(null);

			try {
				const url = `${vouchersIndexRoute}/${voucherId}/orders?page=${page}&limit=${limit}`;
				const response = await fetch(url, {
					headers: { "X-Requested-With": "XMLHttpRequest" },
					signal: controller.signal,
				});

				if (!response.ok) {
					throw new Error(`Request failed with status ${response.status}`);
				}

				const data: VoucherOrdersResponse = await response.json();
				setOrders(Array.isArray(data.orders) ? data.orders : []);
				setMetadata(data.metadata ?? null);
			} catch (err) {
				if (err instanceof DOMException && err.name === "AbortError") return;
				console.error("Failed to load voucher orders:", err);
				setError("Failed to load orders. Please try again.");
				setOrders([]);
				setMetadata(null);
			} finally {
				setIsLoading(false);
			}
		};

		fetchOrders();

		return () => controller.abort();
	}, [isOpen, voucherId, vouchersIndexRoute, page, limit]);

	const openOrder = useCallback(
		(registrationNumber: string) => {
			if (!registrationNumber) return;
			const url = `${ordersIndexRoute}?search=${encodeURIComponent(registrationNumber)}`;
			window.open(url, "_blank", "noopener,noreferrer");
		},
		[ordersIndexRoute],
	);

	const formatDate = useCallback(
		(value: string | null) =>
			value ? format(new Date(value), "PPp", { locale, in: tzDate }) : "N/A",
		[locale, tzDate],
	);

	const title = `Orders using ${voucherCode}`;
	const description =
		metadata?.count != null
			? `${metadata.count} order(s) used this voucher. Tap an order to open it in a new tab.`
			: "Orders where this voucher has been applied. Tap an order to open it in a new tab.";

	const listContent = isLoading ? (
		<div className="space-y-2">
			{["s1", "s2", "s3"].map((key) => (
				<Skeleton key={key} className="w-full h-24 rounded-lg" />
			))}
		</div>
	) : error ? (
		<div className="py-10 text-sm text-center text-destructive">{error}</div>
	) : orders.length === 0 ? (
		<div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
			<ReceiptTextIcon className="opacity-50 size-8" />
			<p className="text-sm">No orders have used this voucher yet.</p>
		</div>
	) : (
		<div className="flex flex-col gap-2">
			{orders.map((order) => (
				<OrderCard
					key={order.id}
					order={order}
					onOpen={openOrder}
					formatDate={formatDate}
				/>
			))}
		</div>
	);

	const paginationContent =
		!isLoading && !error && metadata && orders.length > 0 ? (
			<ApptPagination
				metadata={metadata}
				label="orders"
				actions={{
					goToPrevpage: () => setPage((prev) => Math.max(prev - 1, 1)),
					goToNextPage: () =>
						setPage((prev) =>
							metadata.pages ? Math.min(prev + 1, metadata.pages) : prev + 1,
						),
					onChangeLimit: (value) => {
						setLimit(Number(value));
						setPage(1);
					},
					goToPage: (value) => setPage(Number(value)),
					goToFirstPage: () => setPage(1),
					goToLastPage: () => setPage(metadata.pages ?? 1),
				}}
			/>
		) : null;

	if (isDesktop) {
		return (
			<Dialog open={isOpen} onOpenChange={onOpenChange} modal>
				<DialogContent className="flex flex-col gap-0 p-0 overflow-hidden max-h-[85dvh] sm:max-w-2xl">
					<DialogHeader className="p-6 pb-4 space-y-1 text-left border-b shrink-0">
						<DialogTitle>{title}</DialogTitle>
						<DialogDescription className="text-pretty">
							{description}
						</DialogDescription>
					</DialogHeader>

					<div className="flex-1 min-h-0 p-6 py-4 overflow-y-auto overscroll-contain">
						{listContent}
					</div>

					<div className="flex flex-col gap-3 p-4 border-t shrink-0">
						{paginationContent}

						<div className="flex justify-end">
							<DialogClose asChild>
								<Button variant="outline" size="sm" className="w-fit">
									Close
								</Button>
							</DialogClose>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		);
	}

	return (
		// dismissible={false} disables swipe-down/backdrop close so the inner
		// scroll area doesn't fight the drawer drag gesture; the footer Close
		// button is the single, always-visible dismiss path.
		<Drawer open={isOpen} onOpenChange={onOpenChange} dismissible={false}>
			<DrawerContent className="max-h-[100dvh]">
				<DrawerHeader className="text-left shrink-0">
					<DrawerTitle>{title}</DrawerTitle>
					<DrawerDescription>{description}</DrawerDescription>
				</DrawerHeader>

				<div className="flex-1 min-h-0 px-4 pt-4 pb-6 overflow-y-auto overscroll-contain">
					{listContent}
				</div>

				<DrawerFooter className="gap-3 pt-3 border-t shrink-0">
					{paginationContent}

					<Button
						variant="outline"
						className="w-full shadow-none"
						onClick={() => onOpenChange(false)}
					>
						Close
					</Button>
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	);
}
